import PropTypes from 'prop-types';
import React from 'react';
import { Statistics, Numbers, CircleProgressBar, Icon, Tooltip } from '@weezevent/nacre';
import i18n from 'i18next';

import { useTranslation } from 'react-i18next';
import { Loader } from 'semantic-ui-react';
import Styles from '../../../styles/event/dashboard/eventDashboard.module.css';
import StatisticsTable from './components/statisticsTable';
import StatisticsVariation from './components/statisticsVariation';

export function StatisticsMoreLabel({ title }) {
    const { t } = useTranslation();

    return (
        <div className={Styles['statistics-more-label-container']}>
            <span className={Styles['statistics-more-label']}>{title || t('ticket.eventDashboard.moreDetails')}</span>
            <Icon name={'chevron-right'} size={'small'} />
        </div>
    );
}

StatisticsMoreLabel.propTypes = {
    title: PropTypes.string
};

export default function StatisticsAttendees({ data = null, currency = 'EUR', titleKey = 'total_attendees', prefixUrl, eventStatus, eventEndDate }) {
    const { t } = useTranslation();
    const locale = i18n.language;

    const statistics = React.useMemo(() => {
        if (!data) {
            return null;
        }

        return data.find(groupStats => groupStats.name === 'attendees_stats').value;
    }, [data]);

    const percentageQuota = React.useMemo(() => {
        if (!statistics) {
            return null;
        }

        if (!statistics.event_quota || +statistics.event_quota === 0) {
            return null;
        }
        return +((statistics[titleKey] * 100) / +statistics.event_quota).toFixed(0);
    }, [statistics]);

    if (!statistics) {
        return (
            <Statistics stat="">
                <Loader active inline="centered" />
            </Statistics>
        );
    }

    const rowsTableTop = {
        attendees_weez_web: {
            title: (
                <Tooltip margin={false} position={'bottom'} triggerOnHover title={t('ticket.eventDashboard.statisticsAttendees.attendeesWeezHelp')}>
                    <span> {t('ticket.eventDashboard.statisticsAttendees.attendeesWeez', { count: +statistics?.attendees_weez_web })} </span>
                </Tooltip>
            ),
            value: (
                <Statistics.CellStyle bold>
                    <Numbers value={statistics.attendees_weez_web} locale={locale} />
                </Statistics.CellStyle>
            )
        },
        attendees_ticket_office: {
            title: <span>{t('ticket.eventDashboard.statisticsAttendees.attendeesTicketOffice', { count: +statistics.attendees_ticket_office })}</span>,
            value: (
                <Statistics.CellStyle bold>
                    <Numbers value={statistics.attendees_ticket_office} locale={locale} />
                </Statistics.CellStyle>
            )
        },
        attendees_invitations: {
            title: <span>{t('ticket.eventDashboard.statisticsAttendees.attendeesInvitations', { count: +statistics.attendees_invitations })}</span>,
            value: (
                <Statistics.CellStyle bold>
                    <Numbers value={statistics.attendees_invitations} locale={locale} />
                </Statistics.CellStyle>
            )
        },
        other_weez_channels: {
            title: (
                <Tooltip margin={false} position={'bottom'} triggerOnHover title={t('ticket.eventDashboard.statisticsAttendees.otherWeezChannelsTooltips')}>
                    <span> {t('ticket.eventDashboard.statisticsAttendees.otherWeezChannels', { count: +statistics.other_weez_channels })} </span>
                </Tooltip>
            ),
            value: (
                <Statistics.CellStyle bold>
                    <Numbers value={statistics.other_weez_channels} locale={locale} />
                </Statistics.CellStyle>
            )
        },
        external_channels: {
            title: <span>{t('ticket.eventDashboard.statisticsAttendees.externalChannels', { count: +statistics.external_channels })}</span>,
            value: (
                <Statistics.CellStyle bold>
                    <Numbers value={statistics.external_channels} locale={locale} />
                </Statistics.CellStyle>
            )
        }
        //  Billetterie en ligne Weezevent: {
        //     title: <span>{t(' Billetterie en ligne Weezevent', { count: +statistics.external_channels })}</span>,
        //     value: (
        //         <Statistics.CellStyle bold>
        //             <Numbers value={statistics.external_channels} locale={locale} />
        //         </Statistics.CellStyle>
        //     )
        // }
    };

    const rowsTableBottom = {
        partner_guests: {
            title: (
                <Tooltip margin={false} position={'bottom'} triggerOnHover title={t('ticket.eventDashboard.statisticsAttendees.partnerGuestsHelp')}>
                    <Statistics.CellStyle grey> {t('ticket.eventDashboard.statisticsAttendees.partnerGuests', { count: +statistics.partner_guests })} </Statistics.CellStyle>
                </Tooltip>
            ),
            value: (
                <Statistics.CellStyle bold grey>
                    {`+ `}
                    <Numbers value={statistics.partner_guests} locale={locale} />
                </Statistics.CellStyle>
            )
        },
        attendees_reservation: {
            title: (
                <Statistics.CellStyle grey>
                    {t('ticket.eventDashboard.statisticsAttendees.attendeesReservation', { count: +statistics.attendees_reservation })}
                </Statistics.CellStyle>
            ),
            value: (
                <Statistics.CellStyle bold grey>
                    {`+ `}
                    <Numbers value={statistics.attendees_reservation} locale={locale} />
                </Statistics.CellStyle>
            )
        },
        attendees_in_progress: {
            title: <Statistics.CellStyle grey>{t('ticket.eventDashboard.statisticsAttendees.attendeesInProgress')}</Statistics.CellStyle>,
            value: (
                <Statistics.CellStyle bold grey>
                    {`+ `}
                    <Numbers value={statistics.attendees_in_progress} locale={locale} />
                </Statistics.CellStyle>
            )
        }
    };

    return (
        <Statistics
            graph={percentageQuota !== null ? <CircleProgressBar percentage={percentageQuota} strokeColor={'rgb(var(--primary-color))'} trailStrokeColor={'#D3D3D3'} /> : null}
            className={Styles['statistics-event-dashboard-card']}
            subtitle={t('ticket.eventDashboard.statisticsAttendees.subtitle', { count: +statistics[titleKey] })}
            stat={<Numbers value={statistics[titleKey]} locale={locale} />}
            statMax={+statistics.event_quota ? <Numbers value={+statistics.event_quota} locale={locale} /> : null}
            moreURL={`${prefixUrl}/manager/attendees`}
            moreLabel={+statistics[titleKey] > 0 ? <StatisticsMoreLabel title={t('attendees.manage')} /> : null}
            footer={
                statistics[titleKey] > 0 && (
                    <StatisticsVariation
                        total={statistics[titleKey]}
                        variationUp={statistics.variation_last_week_up}
                        variationDown={statistics.variation_last_week_down}
                        variationUnit={'%'}
                        variationLabel={t('ticket.eventDashboard.statisticsAttendees.variationLabel')}
                        eventStatus={eventStatus}
                        eventEndDate={eventEndDate}
                    />
                )
            }
        >
            {statistics[titleKey] > 0 && (
                <Statistics.Content className={Styles['content-statistics-table-bottom-special']}>
                    <StatisticsTable statistics={statistics} rows={rowsTableTop} />
                    <StatisticsTable statistics={statistics} rows={rowsTableBottom} className={Styles['statistics-table-bottom']} />
                </Statistics.Content>
            )}
        </Statistics>
    );
}

StatisticsAttendees.propTypes = {
    data: PropTypes.array,
    currency: PropTypes.string,
    titleKey: PropTypes.string,
    prefixUrl: PropTypes.string.isRequired,
    eventStatus: PropTypes.string.isRequired,
    eventEndDate: PropTypes.string
};
